<template>
  <div>
    <h3>General description</h3>
    <p>
      <i
        >Perturbations are not available in
        <b>Maneuvering Strategy Design</b> missions.</i
      >
      <br />
      <i
        >Perturbations are limited in <b>RAAN Phasing</b>,
        <b>Orbit Phasing</b> and <b>LEO Station Keeping</b> missions.</i
      >
    </p>

    <h3>Earth potential:</h3>
    <p>
      The EGM96 model is used. The default values for the degree
      <katex-element expression="n" /> and order
      <katex-element expression="m" /> of the model depend on the orbit's
      semi-major axis <katex-element expression="a" /> as reported below:
      <br />
      <katex-element
        expression="(n, m) = (8, 8) \text{  for  } a \leq 13000 \text{ km}"
      />;<br />
      <katex-element
        expression="(n, m) = (6, 6) \text{  for  } 13000 \text{ km} < a < 26000 \text{ km}"
      />;<br />
      <katex-element
        expression="(n, m) = (4, 4) \text{  for  }a \geq 26000 \text{ km}"
      />.<br />
      <br />
      These values can be changed by the user by using a custom
      configuration.<br />
    </p>

    <h3>Third body:</h3>
    <p>
      The perturbative effect of Sun and Moon can be considered. JPL ephemerides
      are used to compute bodies' positions and velocities at a given date.
      <br />
      Note: As of now, when this perturbation is taken into account, the end
      date of the mission must be before 2068 (because the ephemerides of the
      two perturbing bodies have been limited to this date).
      <br />
    </p>
    <h3>Atmospheric drag:</h3>
    <p>
      The atmospheric drag heavily affects the spacecraft’s motion in LEO. The
      acceleration due to drag is given by:
    </p>
    <div class="text-xs-center">
      <katex-element
        expression="\mathbf{a}_{drag}=-0.5 (C_D\frac{A}{m})\rho v_{rel}\mathbf{v_{rel}}"
      />
    </div>

    <p>
      Where:
      <br />
      <katex-element expression="\mathbf{v_{rel}}" />
      : the relative velocity of the spacecraft with respect to the atmosphere.
      <br />
      <katex-element expression="C_D" />
      : the drag coefficient, which takes into account the complex interactions
      between the flow and the spacecraft. It is an empirical measure of the
      body's susceptibility to drag forces.
      <br />
      For satellites in the upper atmosphere is often of the order of 2.2 (flat
      plate approximation).
    </p>

    <p>
      The model considered for simulating the Earth's atmosphere is the
      Harris-Priester model, which relies on tabulated data in order to estimate
      the value of the atmospheric density. According to the selected value for
      the solar flux, the tables can be interpolated to return a more accurate
      density estimate.
    </p>
    <h3>Solar radiation pressure:</h3>
    <p>
      The incident flux of solar particles from the Sun, is absorbed and then
      reflected by the spacecraft's surface. This interaction will produce a
      force on the spacecraft. Formula are not included here, but the interested
      reader can consult the reference (see below).
      <br />
      The radiative properties of the spacecraft are defined by two
      coefficients:
    <ul>
      <li>Absorption coefficient: must be between 0 and 1</li>
      <li>
        Reflection coefficient: this is the (specular) reflection coefficient
        and must be between 0 and 1.
      </li>
    </ul>
    </p>

    <h3>Unperturbed motion:</h3>
    <p>
      When no perturbation forces are selected, the propagation is equivalent to
      an unperturbed Keplerian motion around a point mass (having the same
      gravitational constant than the Earth). In this case, the orbital elements
      do not change except for the anomaly (angular position on the orbit).
    </p>

    <h3>References</h3>
    <p>
      D.A. Vallado, Fundamentals of Astrodynamics and Applications, 4th ed,
      2014, Space Technology Library.
    </p>
  </div>
</template>

<script>
export default {
  name: 'tooltipPerturbations'
}
</script>
